import { Link } from "react-router-dom";
import { useBriefDraftReadiness, useDraftBrief } from "../../hooks/useBriefDraft";
import { campaignStatusActions, useSetCampaignStatus } from "../../hooks/useCampaignStatus";
import { brandSurfacesPath, surfaceLabel } from "../../lib/surface-paths";
import { buildCorpusPath } from "../../lib/brand-navigation";

const DRAFTABLE_STATES = new Set(["briefed", "ideation"]);
const errorCode = (error: unknown) => (error && typeof error === "object" && typeof (error as { code?: unknown }).code === "string" ? (error as { code: string }).code : null);

/** Where to fix a refusal: the server's in-app `action`, or the brand's Corpus for a missing source. Only in-app paths become links. */
function errorAction(error: unknown, brandId: string): { label: string; path: string } | null {
  const details = error && typeof error === "object" ? (error as { details?: unknown }).details : null;
  const action = details && typeof details === "object" ? (details as { action?: unknown }).action : null;
  if (action && typeof action === "object") {
    const { label, path } = action as { label?: unknown; path?: unknown };
    if (typeof label === "string" && label.trim() && typeof path === "string" && path.startsWith("/") && !path.startsWith("//")) return { label, path };
  }
  return errorCode(error) === "NO_QUALIFYING_SOURCES" ? { label: "Open Corpus", path: buildCorpusPath(brandId) } : null;
}

/** PRD-0189 C: one control that turns a `briefed` brief into a draft that flows into Review gates. */
export function DraftThisBrief({ brandId, campaignId, brief, campaign }: {
  brandId: string;
  campaignId: string;
  brief: { id: string; state: string; surface: string | null };
  /** When known, an inactive campaign is explained (and can be activated) before the operator clicks. */
  campaign?: { status: string; updated_at?: string | null };
}) {
  const draft = useDraftBrief(brandId, campaignId);
  const activate = useSetCampaignStatus(brandId, campaignId);
  const draftable = DRAFTABLE_STATES.has(brief.state);
  // LinkedIn/YouTube drafts need an active brand surface. Ask the server's own check before the click,
  // so the operator sees why instead of an error afterwards. A failed check never blocks: the server enforces.
  const readiness = useBriefDraftReadiness(campaignId, draftable ? brief.id : undefined, brief.surface);
  const surfaceMissing = readiness.data?.surface_required === true && readiness.data.surface_active === false;
  // Round-3 P0-2: an `ideation` brief (stranded by the old Review retry) can be drafted too; the server
  // returns it to briefed when its canonical brief is complete, or explains what is missing.
  // Blocked briefs are retried from the blocked banner and the latest-attempt panel.
  if (!draftable) return null;
  const result = draft.data;
  const blog = brief.surface === "blog";
  const message = !result ? null
    : result.status === "already_drafted" ? "This brief already has a draft."
    : `Drafting${result.status === "running" ? " is already in progress" : ""}. ${blog
      ? "Blog drafts take a few minutes and appear in Review only when quality checks pass."
      : "The draft goes through voice review, then appears in Review."}`;
  const activated = activate.isSuccess;
  const inactive = !activated && ((campaign && campaign.status !== "active") || (draft.isError && errorCode(draft.error) === "CAMPAIGN_NOT_ACTIVE"));
  // Only a draft or paused campaign can be (re)activated here; completed and archived campaigns stay closed.
  const offer = campaign ? campaignStatusActions(campaign.status).find(action => action.to === "active") ?? null
    : { label: "Activate campaign", pendingLabel: "Activating…" };
  const notice = campaign?.status === "paused" ? "This campaign is paused. Resume it before drafting its briefs."
    : campaign && !offer ? `This campaign is ${campaign.status}; its briefs can no longer be drafted.`
    : "Activate the campaign before drafting its briefs.";
  const activateCampaign = () => {
    activate.mutate({ status: "active", ...(campaign?.updated_at ? { expected_updated_at: campaign.updated_at } : {}) },
      { onSuccess: () => draft.reset() });
  };
  return (
    <section aria-label="Draft this brief" className="border-t border-slate-200 px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={draft.isPending || !!inactive || surfaceMissing || result?.status === "started" || result?.status === "running"}
          onClick={() => draft.mutate(brief.id)}
          className="min-h-11 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {draft.isPending ? "Starting…" : "Draft this"}
        </button>
        <p className="text-sm text-slate-600">Writes a draft from this brief. Nothing is published; you approve it in Review.</p>
      </div>
      {inactive && <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 text-sm text-amber-900">
        <span>{notice}</span>
        {offer && <button type="button" disabled={activate.isPending} onClick={activateCampaign}
          className="min-h-11 rounded-lg border border-brand-300 bg-white px-3 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50">
          {activate.isPending ? offer.pendingLabel : offer.label}
        </button>}
        {activate.isError && <span>{activate.error instanceof Error ? activate.error.message : "The campaign could not be activated."}</span>}
      </div>}
      {activated && !result && <p role="status" className="mt-3 text-sm text-emerald-800">Campaign activated. You can draft this brief now.</p>}
      {message && <p role="status" className="mt-3 text-sm text-emerald-800">{message}{result?.run_id && <> <Link to={`/runs/${encodeURIComponent(result.run_id)}`} className="font-medium text-brand-700 underline">View run</Link></>}</p>}
      {!inactive && (surfaceMissing || (draft.isError && errorCode(draft.error) === "SURFACE_NOT_ACTIVE"))
        ? <p role="alert" className="mt-3 text-sm text-amber-900">This brand has no active {surfaceLabel(brief.surface)} surface, so this brief cannot be drafted yet. Add or activate it, then try again.{" "}
          <Link to={brandSurfacesPath(brandId)} className="font-medium text-brand-700 underline">Add surface</Link></p>
        : draft.isError && !inactive && (() => {
          const fix = errorAction(draft.error, brandId);
          return <p role="alert" className="mt-3 text-sm text-amber-900">{draft.error instanceof Error ? draft.error.message : "The draft could not start."}
            {fix && <> <Link to={fix.path} className="font-medium text-brand-700 underline">{fix.label}</Link></>}</p>;
        })()}
    </section>
  );
}
